import React, { useEffect, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import JobOfferTable, { COLUMNS_CONSTANTS as JOB_OFFER_COLUMNS } from "./job_offer/JobOfferTable";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import NotesDrawer from "bundles/Internal/components/common/NotesDrawer";
import exportCsv from "bundles/common/utils/exportCsv";
import {
  EMPTY_JOB_OFFER_FILTERS,
} from "bundles/Internal/components/job_offer/JobOfferTableFilters";

function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.company_industries?.length) {
    filters.company_industries = activeFilters.company_industries;
    hasAny = true;
  }

  if (activeFilters.locations?.length) {
    filters.locations = activeFilters.locations;
    hasAny = true;
  }

  if (activeFilters.job_statuses?.length) {
    filters.job_statuses = activeFilters.job_statuses;
    hasAny = true;
  }

  if (activeFilters.offer_status?.length) {
    filters.offer_status = activeFilters.offer_status;
    hasAny = true;
  }

  if (activeFilters.offer_language?.length) {
    filters.offer_language = activeFilters.offer_language;
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const JobOffersIndexPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobOffers, setJobOffers] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesJobOffer, setNotesJobOffer] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_JOB_OFFER_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const fetchJobOffers = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/job_offers", {
      params: {
        q: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setJobOffers(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch job offers", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchJobOffers(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchJobOffers, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchJobOffers(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchJobOffers, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleExportCsv = useCallback((ids) => {
    exportCsv("job_offers", ids, authenticity_token, JOB_OFFER_COLUMNS)
      .then(() => enqueueSnackbar("CSV exported", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to export CSV", { variant: "error" }));
  }, [authenticity_token, enqueueSnackbar]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <JobOfferTable
        jobOffers={jobOffers}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        onJobOfferSelect={(jobOffer) => {
          if (!jobOffer?.id) return;
          navigate(`/internal/job_offers/${jobOffer.id}`, {
            state: { jobOffer, from: location.pathname },
          });
        }}
        onOpenNotes={(jobOffer) => {
          setNotesJobOffer(jobOffer);
          setNotesOpen(true);
        }}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectable
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        onExportCsv={handleExportCsv}
      />
      <NotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        resource="job_offers"
        record={notesJobOffer}
        onNoteAdded={(jobOfferId) => {
          setJobOffers((prev) =>
            prev.map((jo) =>
              String(jo?.id) === String(jobOfferId)
                ? { ...jo, notes_count: (Number(jo.notes_count) || 0) + 1 }
                : jo
            )
          );
        }}
        onNoteArchived={(jobOfferId) => {
          setJobOffers((prev) =>
            prev.map((jo) =>
              String(jo?.id) === String(jobOfferId)
                ? { ...jo, notes_count: Math.max((Number(jo.notes_count) || 0) - 1, 0) }
                : jo
            )
          );
        }}
      />
    </Box>
  );
};

export default JobOffersIndexPage;
